/* eslint-disable no-unsafe-optional-chaining */
import React, { useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Api from '../../api';
import type { MarginHealthSummary } from '../../api/marginDashboard';
import AutoCompleteSearch from '../../components/input/AutoCompleteSearch';
import EmptyState from '../../components/list/EmptyState';
import {
  accentColors,
  alertColors,
  mainColors,
  secondaryColors,
  successColors
} from '../../theme/themeConfig';
import { formatDate } from '../../utils/general';
import ProjectInspectionBadge from '../../components/shared/ProjectInspectionBadge';

// ─── Types ───────────────────────────────────────────────────────────────────

type ViewMode = 'list' | 'grid' | 'kanban';
type PerPage = 10 | 20 | 50 | 9999;
type SortDir = 'asc' | 'desc';

type ProjectStatus =
  | 'Scheduled'
  | 'In Progress'
  | 'On Hold'
  | 'Completed'
  | 'Cancelled'
  | 'Contract Updated'
  | 'Re-schedule'
  | 'Cancellation Requested';

type ActiveFilter = ProjectStatus | 'All' | 'All Assigned';

// ─── Assignment helpers ───────────────────────────────────────────────────────

type AssignUrgency = 'urgent' | 'soon' | 'ahead' | 'covered' | 'none';

/**
 * Returns urgency level for upcoming assignments.
 * 'urgent' = missing in next 3 days
 * 'soon'   = missing in 3-10 days
 * 'ahead'  = missing 10+ days out
 * 'covered'= all upcoming dates have someone assigned
 * 'none'   = no upcoming dates (completed/past projects)
 */
function getAssignmentUrgency(assignments: any): AssignUrgency {
  if (!assignments) return 'none';
  const n = (v: any) => (v == null ? 0 : parseInt(String(v), 10) || 0);
  const a = Array.isArray(assignments) ? assignments[0] : assignments;
  if (!a) return 'none';

  const req3 = n(a.required3Days);
  const ass3 = n(a.assigned3Days);
  const reqLt10 = n(a.requiredLess10Days);
  const assLt10 = n(a.assignedLess10Days);
  const reqGt10 = n(a.requiredMore10Days);
  const assGt10 = n(a.assignedMore10Days);

  const totalRequired = req3 + reqLt10 + reqGt10;
  if (totalRequired === 0) return 'none';

  if (req3 > 0 && ass3 < req3) return 'urgent';
  if (reqLt10 > 0 && assLt10 < reqLt10) return 'soon';
  if (reqGt10 > 0 && assGt10 < reqGt10) return 'ahead';
  return 'covered';
}

/**
 * Progress based on elapsed contract days.
 * Returns 0–100. Clamps at 100 for overdue projects.
 */
function getDateProgress(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}
function isFullyAssigned(assignments: any): boolean {
  return getAssignmentUrgency(assignments) === 'covered';
}

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { bg: string; color: string; dot: string }
> = {
  'Cancellation Requested': { bg: '#FEE2E2', color: '#DC2626', dot: '#DC2626' },
  Cancelled: { bg: '#FEE2E2', color: '#B91C1C', dot: '#B91C1C' },
  Completed: {
    bg: successColors.SUCCESS_6,
    color: successColors.SUCCESS,
    dot: successColors.SUCCESS_7
  },
  'Contract Updated': {
    bg: accentColors.ACCENT_6,
    color: accentColors.ACCENT,
    dot: accentColors.ACCENT
  },
  'In Progress': {
    bg: mainColors.MAIN_LIGHT,
    color: mainColors.MAIN,
    dot: mainColors.MAIN
  },
  'On Hold': {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  },
  'Re-schedule': {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT_7,
    dot: alertColors.ALERT_7
  },
  Scheduled: { bg: '#EDE9FE', color: '#7C3AED', dot: '#7C3AED' }
};

const KANBAN_STATUSES: ProjectStatus[] = [
  'Scheduled',
  'In Progress',
  'On Hold',
  'Completed'
];

const FILTER_PILLS: { description?: string; label: ActiveFilter }[] = [
  { label: 'All' },
  {
    description: 'Projects with all service dates assigned',
    label: 'All Assigned'
  },
  { label: 'In Progress' },
  { label: 'On Hold' },
  { label: 'Completed' },
  { label: 'Cancelled' }
];

const PER_PAGE_OPTIONS: { label: string; value: PerPage }[] = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: 'All', value: 9999 }
];

const AVATAR_COLORS = [
  mainColors.MAIN,
  mainColors.MAIN_2,
  accentColors.ACCENT,
  successColors.SUCCESS,
  '#7C3AED',
  '#0F766E'
];

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageContainer = styled(Box)(() => ({
  padding: '24px'
}));

const ToolbarBox = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '16px'
}));

const StatusPill = styled(Box)(() => ({
  alignItems: 'center',
  borderRadius: '20px',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '12.5px',
  fontWeight: 500,
  gap: '5px',
  padding: '5px 13px',
  transition: 'all .15s',
  whiteSpace: 'nowrap'
}));

const ViewToggleBtn = styled(IconButton)(() => ({
  border: 'none',
  borderRadius: '6px',
  height: '28px',
  width: '30px'
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  background: '#F4F7FB',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '.6px',
  padding: '10px 16px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: mainColors.MAIN_LIGHT },
  '&:last-child td': { borderBottom: 0 },
  cursor: 'pointer'
}));

const TableCard = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  overflow: 'hidden'
}));

const ProjectCard = styled(Box)(() => ({
  '&:hover': {
    boxShadow: '0 8px 28px rgba(0,93,162,.13)',
    transform: 'translateY(-2px)'
  },
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'box-shadow .22s, transform .22s'
}));

const KanbanColumn = styled(Box)(() => ({
  background: '#F4F7FB',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}));

const KanbanCard = styled(Box)(() => ({
  '&:hover': {
    boxShadow: '0 4px 14px rgba(15,28,46,.1)',
    transform: 'translateY(-1px)'
  },
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '9px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  cursor: 'pointer',
  padding: '12px',
  transition: 'box-shadow .18s, transform .18s'
}));

const PagBar = styled(Box)(() => ({
  alignItems: 'center',
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '16px',
  padding: '12px 18px'
}));

const PgButton = styled(Box)(() => ({
  alignItems: 'center',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '13px',
  fontWeight: 500,
  height: '30px',
  justifyContent: 'center',
  minWidth: '32px',
  padding: '0 6px',
  transition: 'all .12s',
  userSelect: 'none' as const
}));

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatusChipProps {
  status: string;
}
function StatusChip({ status }: StatusChipProps) {
  const cfg = STATUS_CONFIG[status] ?? {
    bg: secondaryColors.SECONDARY_6,
    color: secondaryColors.SECONDARY,
    dot: secondaryColors.SECONDARY_1
  };
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: cfg.bg,
        borderRadius: '20px',
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 600,
        gap: '5px',
        px: '9px',
        py: '3px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        sx={{
          background: cfg.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: '6px',
          width: '6px'
        }}
      />
      <span style={{ color: cfg.color }}>{status}</span>
    </Box>
  );
}

const URGENCY_CONFIG: Record<
  AssignUrgency,
  { bg: string; color: string; icon: string; label: string } | null
> = {
  ahead: {
    bg: mainColors.MAIN_LIGHT,
    color: mainColors.MAIN,
    icon: '○',
    label: 'Plan ahead — 10+ days out'
  },
  covered: {
    bg: successColors.SUCCESS_6,
    color: successColors.SUCCESS_7,
    icon: '✓',
    label: 'All covered'
  },
  none: null,
  soon: {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT_7,
    icon: '!',
    label: 'Needs attention — within 10 days'
  },
  urgent: {
    bg: '#FEE2E2',
    color: '#B91C1C',
    icon: '⚠',
    label: 'Urgent — next 3 days'
  }
};

interface AssignBadgeProps {
  assignments: any;
}
function AssignBadge({ assignments }: AssignBadgeProps) {
  const urgency = getAssignmentUrgency(assignments);
  const cfg = URGENCY_CONFIG[urgency];
  if (!cfg) {
    return (
      <Typography sx={{ color: secondaryColors.SECONDARY_3, fontSize: '12px' }}>
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={cfg.label}>
      <Box
        sx={{
          alignItems: 'center',
          background: cfg.bg,
          borderRadius: '20px',
          display: 'inline-flex',
          fontSize: '11px',
          fontWeight: 700,
          gap: '4px',
          px: '9px',
          py: '3px',
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ color: cfg.color }}>
          {cfg.icon} {cfg.label.split(' —')[0]}
        </span>
      </Box>
    </Tooltip>
  );
}

interface DateRangeProps {
  compact?: boolean;
  end?: string;
  start?: string;
}
function DateRange({ start, end, compact }: DateRangeProps) {
  const fmt = (d?: string) => (d && d !== 'Invalid Date' ? formatDate(d) : '—');
  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '5px' }}>
          <Box
            sx={{
              background: successColors.SUCCESS_7,
              borderRadius: '50%',
              flexShrink: 0,
              height: '5px',
              width: '5px'
            }}
          />
          <Typography
            sx={{
              color: secondaryColors.SECONDARY_3,
              fontSize: '9.5px',
              fontWeight: 700,
              textTransform: 'uppercase',
              width: '26px'
            }}
          >
            Start
          </Typography>
          <Typography
            sx={{ color: '#0F1C2E', fontSize: '11.5px', fontWeight: 600 }}
          >
            {fmt(start)}
          </Typography>
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '5px' }}>
          <Box
            sx={{
              background: secondaryColors.SECONDARY_1,
              borderRadius: '50%',
              flexShrink: 0,
              height: '5px',
              width: '5px'
            }}
          />
          <Typography
            sx={{
              color: secondaryColors.SECONDARY_3,
              fontSize: '9.5px',
              fontWeight: 700,
              textTransform: 'uppercase',
              width: '26px'
            }}
          >
            End
          </Typography>
          <Typography
            sx={{ color: '#0F1C2E', fontSize: '11.5px', fontWeight: 600 }}
          >
            {fmt(end)}
          </Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: '#F4F7FB',
        border: `1px solid ${secondaryColors.SECONDARY_6}`,
        borderRadius: '7px',
        display: 'flex',
        gap: '8px',
        mb: 1.5,
        px: 1.25,
        py: 0.75
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            color: secondaryColors.SECONDARY_3,
            fontSize: '9.5px',
            fontWeight: 700,
            letterSpacing: '.5px',
            textTransform: 'uppercase'
          }}
        >
          Start
        </Typography>
        <Typography
          sx={{
            color: '#0F1C2E',
            fontSize: '12px',
            fontWeight: 700,
            mt: '1px'
          }}
        >
          {fmt(start)}
        </Typography>
      </Box>
      <Typography sx={{ color: secondaryColors.SECONDARY_1, fontSize: '13px' }}>
        →
      </Typography>
      <Box sx={{ flex: 1, textAlign: 'right' }}>
        <Typography
          sx={{
            color: secondaryColors.SECONDARY_3,
            fontSize: '9.5px',
            fontWeight: 700,
            letterSpacing: '.5px',
            textTransform: 'uppercase'
          }}
        >
          End
        </Typography>
        <Typography
          sx={{
            color: '#0F1C2E',
            fontSize: '12px',
            fontWeight: 700,
            mt: '1px'
          }}
        >
          {fmt(end)}
        </Typography>
      </Box>
    </Box>
  );
}

DateRange.defaultProps = {
  compact: false,
  end: undefined,
  start: undefined
};

interface SortIconProps {
  activeDir: SortDir;
  activeField: string;
  field: string;
}
function SortIcon({ field, activeField, activeDir }: SortIconProps) {
  if (activeField !== field)
    return <ArrowDownwardIcon sx={{ fontSize: 11, ml: 0.5, opacity: 0.25 }} />;
  return activeDir === 'asc' ? (
    <ArrowUpwardIcon sx={{ color: mainColors.MAIN, fontSize: 11, ml: 0.5 }} />
  ) : (
    <ArrowDownwardIcon sx={{ color: mainColors.MAIN, fontSize: 11, ml: 0.5 }} />
  );
}

function getInitials(name: string) {
  const parts = (name ?? '').trim().split(' ');
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

interface ProjectAvatarProps {
  index?: number;
  name: string;
  size?: number;
}
function ProjectAvatar({ name, size, index }: ProjectAvatarProps) {
  const bg = AVATAR_COLORS[(index ?? 0) % AVATAR_COLORS.length];
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: bg,
        borderRadius: '10px',
        color: '#fff',
        display: 'flex',
        flexShrink: 0,
        fontSize: (size ?? 40) * 0.33,
        fontWeight: 700,
        height: size ?? 40,
        justifyContent: 'center',
        width: size ?? 40
      }}
    >
      {getInitials(name)}
    </Box>
  );
}

ProjectAvatar.defaultProps = {
  index: 0,
  size: 40
};

// ─── Health gauge sub-component ──────────────────────────────────────────────

interface HealthGaugeCardProps {
  health: { color: string; integrityScore: number } | null | undefined;
  onClick: React.MouseEventHandler;
}
function HealthGaugeCard({ health, onClick }: HealthGaugeCardProps) {
  const healthColorMap: Record<string, string> = {
    AMBER: '#E79137',
    GREEN: '#68A550',
    RED: '#CE5A55'
  };

  const label = (
    <Typography
      sx={{
        color: secondaryColors.SECONDARY_3,
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '.4px',
        textAlign: 'right',
        textTransform: 'uppercase'
      }}
    >
      Health
    </Typography>
  );

  if (!health) {
    return (
      <Box>
        {label}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: '2px'
          }}
        >
          <svg height="26" viewBox="0 0 36 26" width="36">
            <path
              d="M4,22 A14,14 0 0,1 32,22"
              fill="none"
              stroke="#E0E0E0"
              strokeLinecap="round"
              strokeWidth="3.5"
            />
          </svg>
        </Box>
      </Box>
    );
  }

  const gaugeColor = healthColorMap[health.color] || '#E0E0E0';
  const score = health.integrityScore;
  const radius = 14;
  const arcLength = Math.PI * radius;
  const fillLength = (score / 100) * arcLength;
  const dashArray = `${fillLength} ${arcLength}`;

  return (
    <Tooltip arrow title={`Margin Health: ${score}`}>
      <Box
        onClick={onClick}
        sx={{
          '&:hover': { transform: 'scale(1.1)' },
          cursor: 'pointer',
          transition: 'transform 0.15s'
        }}
      >
        {label}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: '2px'
          }}
        >
          <svg height="26" viewBox="0 0 36 26" width="36">
            <path
              d="M4,22 A14,14 0 0,1 32,22"
              fill="none"
              stroke="#E8E8E8"
              strokeLinecap="round"
              strokeWidth="3.5"
            />
            <path
              d="M4,22 A14,14 0 0,1 32,22"
              fill="none"
              stroke={gaugeColor}
              strokeDasharray={dashArray}
              strokeLinecap="round"
              strokeWidth="3.5"
            />
            <text
              dominantBaseline="middle"
              fill={gaugeColor}
              fontSize="10"
              fontWeight="700"
              textAnchor="middle"
              x="18"
              y="21"
            >
              {score}
            </text>
          </svg>
        </Box>
      </Box>
    </Tooltip>
  );
}

// ─── Stat tile config ─────────────────────────────────────────────────────────

// ─── Main component ───────────────────────────────────────────────────────────

const ListingProjects: React.FC = function ListingProjects() {
  const [textSearchFilter, setTextSearchFilter] = useState('');
  const [options, setOptions] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('All');
  const [perPage, setPerPage] = useState<PerPage>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [dataProject, setDataProject] = useState<any[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [statCounts, setStatCounts] = useState<Record<string, number>>({
    All: 0,
    'All Assigned': 0,
    Completed: 0,
    'In Progress': 0,
    'On Hold': 0
  });
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState('leadProjectStartDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [healthMap, setHealthMap] = useState<
    Record<string, { color: string; integrityScore: number }>
  >({});

  const navigate = useNavigate();
  const getAction = Api.ProjectApi.getProjectsPaginated;

  // ── Main data — backend does not support status filter, all filtering is client-side ──
  useEffect(() => {
    let cancelled = false;
    setIsValidating(true);
    setDataProject([]);
    getAction({
      items: perPage,
      page: currentPage,
      ...(textSearchFilter ? { searchText: textSearchFilter } : {})
    })
      .then((res: any) => {
        if (cancelled || !res) return;
        const newData = res.data ?? [];
        setDataProject(newData);
        setTotalItems(res.pagination?.totalItems ?? 0);
        setIsValidating(false);
      })
      .catch(() => {
        if (!cancelled) {
          setDataProject([]);
          setIsValidating(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentPage, perPage, textSearchFilter]);

  // ── Derive stat counts from loaded data ──
  useEffect(() => {
    setStatCounts({
      All: dataProject.length,
      'All Assigned': dataProject.filter((p: any) =>
        isFullyAssigned(p.assignments)
      ).length,
      Completed: dataProject.filter((p: any) => p.status === 'Completed')
        .length,
      'In Progress': dataProject.filter((p: any) => p.status === 'In Progress')
        .length,
      'On Hold': dataProject.filter((p: any) => p.status === 'On Hold').length
    });
  }, [dataProject]);

  // ── Fetch margin health scores for loaded projects ──
  useEffect(() => {
    if (!dataProject || dataProject.length === 0) return;
    const newProjects = dataProject.filter(
      (p: any) => p.id && !healthMap[p.id]
    );
    if (newProjects.length === 0) return;
    const projectDates = newProjects.map((p: any) => ({
      end: dayjs(p.leadProjectEndDate).format('YYYY-MM-DD'),
      id: p.id,
      start: dayjs(p.leadProjectStartDate).format('YYYY-MM-DD')
    }));
    Api.MarginDashboardApi.getMarginHealthBatch({
      projects: JSON.stringify(projectDates)
    })
      .then((results: MarginHealthSummary[]) => {
        setHealthMap((prev) => {
          const newMap = { ...prev };
          results.forEach((r) => {
            newMap[r.projectId] = {
              color: r.color,
              integrityScore: r.integrityScore
            };
          });
          return newMap;
        });
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('Health batch fetch failed:', err?.message || err);
      });
  }, [dataProject]);

  useEffect(() => {
    setCurrentPage(1);
  }, [textSearchFilter, activeFilter, perPage]);

  // ── Data helpers ──
  const totalPages = perPage === 9999 ? 1 : Math.ceil(totalItems / perPage);

  const enrichedData = dataProject.map((item: any, i: number) => {
    const client =
      item?.clientCompanyName && item.clientCompanyName !== ''
        ? item.clientCompanyName
        : `${item?.clientFirstName ?? ''} ${item?.clientLastName ?? ''}`.trim();
    return { ...item, client, itemIdx: i };
  });

  const filteredData = enrichedData.filter((item: any) => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'All Assigned')
      return isFullyAssigned(item.assignments);
    return item.status === activeFilter;
  });

  const sortedData = [...filteredData].sort((a: any, b: any) => {
    const aVal = a[sortField] ?? '';
    const bVal = b[sortField] ?? '';
    const cmp = String(aVal).localeCompare(String(bVal), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (field: string) => {
    if (sortField === field)
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const goToDetail = (id: string) =>
    navigate(`/app/projects/detail/${id}`, { state: { id } });

  // ─── Stat strip ────────────────────────────────────────────────────────────
  // ─── List view ─────────────────────────────────────────────────────────────
  const renderList = () => (
    <TableCard>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {(
                [
                  { field: 'leadName', label: 'Project' },
                  { field: 'client', label: 'Client' },
                  { field: 'leadProjectStartDate', label: 'Date Range' },
                  { field: 'status', label: 'Status' },
                  { field: null, label: 'Assignments' },
                  { field: null, label: 'Progress' }
                ] as { field: string | null; label: string }[]
              ).map(({ label, field }) => (
                <StyledHeaderCell
                  key={label}
                  onClick={() => field && handleSort(field)}
                  sx={{
                    '&:hover': field ? { color: mainColors.MAIN } : {},
                    cursor: field ? 'pointer' : 'default',
                    userSelect: 'none'
                  }}
                >
                  <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                    {label}
                    {field && (
                      <SortIcon
                        activeDir={sortDir}
                        activeField={sortField}
                        field={field}
                      />
                    )}
                  </Box>
                </StyledHeaderCell>
              ))}
              <StyledHeaderCell sx={{ textAlign: 'right' }}>
                Actions
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((item: any) => (
              <StyledRow key={item.id} onClick={() => goToDetail(item.id)}>
                <TableCell sx={{ maxWidth: 240, py: 1.5 }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                    <ProjectAvatar
                      index={item.itemIdx}
                      name={item.leadName ?? ''}
                      size={34}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: '#0F1C2E',
                          fontSize: '13px',
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.leadName}
                      </Typography>
                      <Typography
                        sx={{
                          color: secondaryColors.SECONDARY,
                          fontSize: '11px',
                          mt: '1px'
                        }}
                      >
                        {item.projectType === 'Regular'
                          ? '↻ Recurring'
                          : '◆ One-time'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography
                    sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}
                  >
                    {item.client}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <DateRange
                    compact
                    end={item.leadProjectEndDate}
                    start={item.leadProjectStartDate}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <StatusChip status={item.status ?? ''} />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <AssignBadge assignments={item.assignments} />
                </TableCell>
                <TableCell sx={{ minWidth: 110, py: 1.5 }}>
                  {(() => {
                    const pct = getDateProgress(
                      item.leadProjectStartDate,
                      item.leadProjectEndDate
                    );
                    return (
                      <Box
                        sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                      >
                        <Box
                          sx={{
                            background: secondaryColors.SECONDARY_6,
                            borderRadius: '10px',
                            flex: 1,
                            height: '4px',
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              background:
                                STATUS_CONFIG[item.status]?.dot ??
                                mainColors.MAIN,
                              borderRadius: '10px',
                              height: '100%',
                              width: `${pct}%`
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: '#0F1C2E',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {pct}%
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell
                  onClick={(e) => e.stopPropagation()}
                  sx={{ py: 1.5 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      justifyContent: 'flex-end'
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          goToDetail(item.id);
                        }}
                        size="small"
                        sx={{
                          border: `1px solid ${secondaryColors.SECONDARY_6}`,
                          borderRadius: '6px',
                          color: secondaryColors.SECONDARY_1,
                          height: 28,
                          width: 28
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open details">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          goToDetail(item.id);
                        }}
                        size="small"
                        sx={{
                          border: `1px solid ${secondaryColors.SECONDARY_6}`,
                          borderRadius: '6px',
                          color: secondaryColors.SECONDARY_1,
                          height: 28,
                          width: 28
                        }}
                      >
                        <MoreHorizIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedData.length === 0 && !isValidating && <EmptyState />}
    </TableCard>
  );

  // ─── Grid view ─────────────────────────────────────────────────────────────
  const renderGrid = () => (
    <Grid container spacing={1.75}>
      {sortedData.map((item: any) => {
        const cfg = STATUS_CONFIG[item.status] ?? { dot: mainColors.MAIN };
        const urgency = getAssignmentUrgency(item.assignments);
        const urgencyCfg = URGENCY_CONFIG[urgency];
        return (
          <Grid item key={item.id} lg={4} md={6} sm={6} xs={12}>
            <ProjectCard onClick={() => goToDetail(item.id)}>
              <Box sx={{ background: cfg.dot, height: '3px', opacity: 0.7 }} />
              <Box sx={{ flex: 1, p: 2 }}>
                <Box
                  sx={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    gap: 1.5,
                    mb: 1.5
                  }}
                >
                  <ProjectAvatar
                    index={item.itemIdx}
                    name={item.leadName ?? ''}
                    size={40}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#0F1C2E',
                        fontSize: '13.5px',
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.leadName}
                    </Typography>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '11.5px',
                        mt: '2px'
                      }}
                    >
                      {item.client}
                    </Typography>
                  </Box>
                  <StatusChip status={item.status ?? ''} />
                </Box>

                <DateRange
                  end={item.leadProjectEndDate}
                  start={item.leadProjectStartDate}
                />

                {(() => {
                  const pct = getDateProgress(
                    item.leadProjectStartDate,
                    item.leadProjectEndDate
                  );
                  return (
                    <Box sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5
                        }}
                      >
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY_3,
                            fontSize: '11px',
                            fontWeight: 500
                          }}
                        >
                          Progress
                        </Typography>
                        <Typography
                          sx={{
                            color: '#0F1C2E',
                            fontSize: '12px',
                            fontWeight: 700
                          }}
                        >
                          {pct}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          background: secondaryColors.SECONDARY_6,
                          borderRadius: '10px',
                          height: '5px',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            background: cfg.dot,
                            borderRadius: '10px',
                            height: '100%',
                            width: `${pct}%`
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })()}

                <Box
                  sx={{
                    alignItems: 'flex-end',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography
                        sx={{
                          color: secondaryColors.SECONDARY_3,
                          fontSize: '10px',
                          fontWeight: 600,
                          letterSpacing: '.4px',
                          textTransform: 'uppercase'
                        }}
                      >
                        Type
                      </Typography>
                      <Typography
                        sx={{
                          color: '#0F1C2E',
                          fontSize: '12px',
                          fontWeight: 700,
                          mt: '1px'
                        }}
                      >
                        {item.projectType === 'Regular'
                          ? 'Recurring'
                          : 'One-time'}
                      </Typography>
                    </Box>
                    {urgencyCfg && (
                      <Box>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY_3,
                            fontSize: '10px',
                            fontWeight: 600,
                            letterSpacing: '.4px',
                            textTransform: 'uppercase'
                          }}
                        >
                          Assign
                        </Typography>
                        <Tooltip title={urgencyCfg.label}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              background: urgencyCfg.bg,
                              borderRadius: '20px',
                              display: 'inline-flex',
                              fontSize: '11px',
                              fontWeight: 700,
                              mt: '3px',
                              px: '8px',
                              py: '2px'
                            }}
                          >
                            <span style={{ color: urgencyCfg.color }}>
                              {urgencyCfg.icon}{' '}
                              {urgencyCfg.label.split(' —')[0]}
                            </span>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  <HealthGaugeCard
                    health={healthMap[item.id]}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      navigate(`/app/projects/detail/${item.id}`, {
                        state: { id: item.id, tab: 'margin-dashboard' }
                      });
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  alignItems: 'center',
                  background: '#F4F7FB',
                  borderTop: `1px solid ${secondaryColors.SECONDARY_6}`,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1
                }}
              >
                <ProjectInspectionBadge projectId={item.id} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        goToDetail(item.id);
                      }}
                      size="small"
                      sx={{
                        border: `1px solid ${secondaryColors.SECONDARY_6}`,
                        borderRadius: '6px',
                        color: secondaryColors.SECONDARY_1,
                        height: 26,
                        width: 26
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open details">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        goToDetail(item.id);
                      }}
                      size="small"
                      sx={{
                        border: `1px solid ${secondaryColors.SECONDARY_6}`,
                        borderRadius: '6px',
                        color: secondaryColors.SECONDARY_1,
                        height: 26,
                        width: 26
                      }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </ProjectCard>
          </Grid>
        );
      })}
      {sortedData.length === 0 && !isValidating && (
        <Grid item xs={12}>
          <EmptyState />
        </Grid>
      )}
    </Grid>
  );

  // ─── Kanban view ───────────────────────────────────────────────────────────
  const renderKanban = () => {
    const colData: Record<string, any[]> = {};
    KANBAN_STATUSES.forEach((s) => {
      colData[s] = [];
    });
    enrichedData.forEach((p: any) => {
      const s = p.status as ProjectStatus;
      if (colData[s]) colData[s].push(p);
    });

    return (
      <Grid container spacing={1.5}>
        {KANBAN_STATUSES.map((status) => {
          const cfg = STATUS_CONFIG[status];
          const cards = colData[status];
          return (
            <Grid item key={status} lg={3} md={6} xs={12}>
              <KanbanColumn>
                <Box
                  sx={{ background: cfg.dot, height: '3px', opacity: 0.8 }}
                />
                <Box
                  sx={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                    display: 'flex',
                    gap: 1,
                    px: 1.75,
                    py: 1.5
                  }}
                >
                  <Box
                    sx={{
                      background: cfg.dot,
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: '9px',
                      width: '9px'
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#0F1C2E',
                      fontSize: '12.5px',
                      fontWeight: 700
                    }}
                  >
                    {status}
                  </Typography>
                  <Box
                    sx={{
                      alignItems: 'center',
                      background: '#fff',
                      border: `1px solid ${secondaryColors.SECONDARY_6}`,
                      borderRadius: '20px',
                      display: 'inline-flex',
                      fontSize: '11px',
                      fontWeight: 700,
                      ml: 'auto',
                      px: '8px',
                      py: '2px'
                    }}
                  >
                    {cards.length}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1.25
                  }}
                >
                  {cards.map((item: any) => {
                    const kbUrgency = getAssignmentUrgency(item.assignments);
                    const kbCfg = URGENCY_CONFIG[kbUrgency];
                    return (
                      <KanbanCard
                        key={item.id}
                        onClick={() => goToDetail(item.id)}
                      >
                        <Typography
                          sx={{
                            color: '#0F1C2E',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            mb: 0.5
                          }}
                        >
                          {item.leadName}
                        </Typography>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '11px',
                            mb: 1
                          }}
                        >
                          {item.client}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                          <Box>
                            <Typography
                              sx={{
                                color: secondaryColors.SECONDARY_3,
                                fontSize: '9px',
                                fontWeight: 700,
                                textTransform: 'uppercase'
                              }}
                            >
                              Start
                            </Typography>
                            <Typography
                              sx={{
                                color: '#0F1C2E',
                                fontSize: '11px',
                                fontWeight: 700
                              }}
                            >
                              {item.leadProjectStartDate
                                ? formatDate(item.leadProjectStartDate)
                                : '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                color: secondaryColors.SECONDARY_3,
                                fontSize: '9px',
                                fontWeight: 700,
                                textTransform: 'uppercase'
                              }}
                            >
                              End
                            </Typography>
                            <Typography
                              sx={{
                                color: '#0F1C2E',
                                fontSize: '11px',
                                fontWeight: 700
                              }}
                            >
                              {item.leadProjectEndDate
                                ? formatDate(item.leadProjectEndDate)
                                : '—'}
                            </Typography>
                          </Box>
                          {kbCfg && (
                            <Box>
                              <Typography
                                sx={{
                                  color: secondaryColors.SECONDARY_3,
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase'
                                }}
                              >
                                Assign
                              </Typography>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  background: kbCfg.bg,
                                  borderRadius: '10px',
                                  display: 'inline-flex',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  mt: '2px',
                                  px: '6px',
                                  py: '1px'
                                }}
                              >
                                <span style={{ color: kbCfg.color }}>
                                  {kbCfg.icon} {kbCfg.label.split(' —')[0]}
                                </span>
                              </Box>
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            background: secondaryColors.SECONDARY_6,
                            borderRadius: '10px',
                            height: '3px',
                            mb: 0.5,
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              background: cfg.dot,
                              borderRadius: '10px',
                              height: '100%',
                              width: `${getDateProgress(
                                item.leadProjectStartDate,
                                item.leadProjectEndDate
                              )}%`
                            }}
                          />
                        </Box>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <Typography
                            sx={{
                              color: secondaryColors.SECONDARY_3,
                              fontSize: '10.5px'
                            }}
                          >
                            {item.projectType === 'Regular'
                              ? '↻ Recurring'
                              : '◆ One-time'}
                          </Typography>
                        </Box>
                      </KanbanCard>
                    );
                  })}
                  {cards.length === 0 && (
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY_1,
                        fontSize: '12px',
                        p: 1,
                        textAlign: 'center'
                      }}
                    >
                      No projects
                    </Typography>
                  )}
                </Box>
              </KanbanColumn>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // ─── Pagination ────────────────────────────────────────────────────────────
  const renderPagination = () => {
    const start = perPage === 9999 ? 1 : (currentPage - 1) * perPage + 1;
    const end =
      perPage === 9999
        ? totalItems
        : Math.min(currentPage * perPage, totalItems);
    const pageNums: number[] = [];
    const maxPageBtns = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageBtns / 2));
    const endPage = Math.min(totalPages, startPage + maxPageBtns - 1);
    if (endPage - startPage < maxPageBtns - 1)
      startPage = Math.max(1, endPage - maxPageBtns + 1);
    for (let i = startPage; i <= endPage; i += 1) pageNums.push(i);

    return (
      <PagBar>
        <Typography
          sx={{ color: secondaryColors.SECONDARY_3, fontSize: '12px' }}
        >
          {totalItems > 0
            ? `Showing ${start}–${end} of ${totalItems} projects`
            : '0 projects'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            color: secondaryColors.SECONDARY,
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}
        >
          Per page:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {PER_PAGE_OPTIONS.map(({ label, value }) => (
            <PgButton
              key={label}
              onClick={() => {
                setPerPage(value);
                setCurrentPage(1);
              }}
              sx={{
                '&:hover':
                  perPage === value
                    ? {}
                    : { borderColor: mainColors.MAIN, color: mainColors.MAIN },
                background: perPage === value ? mainColors.MAIN : '#fff',
                border: `1px solid ${
                  perPage === value
                    ? mainColors.MAIN
                    : secondaryColors.SECONDARY_6
                }`,
                color: perPage === value ? '#fff' : secondaryColors.SECONDARY,
                fontSize: '12px',
                fontWeight: 600,
                minWidth: 42,
                px: 1.25
              }}
            >
              {label}
            </PgButton>
          ))}
        </Box>
        <Box
          sx={{
            background: secondaryColors.SECONDARY_6,
            height: '20px',
            mx: 0.5,
            width: '1px'
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <PgButton
            onClick={() => {
              if (currentPage > 1) setCurrentPage((p) => p - 1);
            }}
            sx={{
              background: '#fff',
              border: `1px solid ${secondaryColors.SECONDARY_6}`,
              color: secondaryColors.SECONDARY,
              cursor: currentPage <= 1 ? 'default' : 'pointer',
              opacity: currentPage <= 1 ? 0.35 : 1
            }}
          >
            ‹
          </PgButton>
          {pageNums.map((n) => (
            <PgButton
              key={n}
              onClick={() => setCurrentPage(n)}
              sx={{
                '&:hover':
                  n === currentPage
                    ? {}
                    : { borderColor: mainColors.MAIN, color: mainColors.MAIN },
                background: n === currentPage ? mainColors.MAIN : '#fff',
                border: `1px solid ${
                  n === currentPage
                    ? mainColors.MAIN
                    : secondaryColors.SECONDARY_6
                }`,
                color: n === currentPage ? '#fff' : secondaryColors.SECONDARY
              }}
            >
              {n}
            </PgButton>
          ))}
          <PgButton
            onClick={() => {
              if (currentPage < totalPages) setCurrentPage((p) => p + 1);
            }}
            sx={{
              background: '#fff',
              border: `1px solid ${secondaryColors.SECONDARY_6}`,
              color: secondaryColors.SECONDARY,
              cursor: currentPage >= totalPages ? 'default' : 'pointer',
              opacity: currentPage >= totalPages ? 0.35 : 1
            }}
          >
            ›
          </PgButton>
        </Box>
      </PagBar>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <PageContainer>
      {/* Toolbar: status pills | spacer | search | sort | view toggle */}
      <ToolbarBox>
        {FILTER_PILLS.map(({ label, description }) => (
          <Tooltip key={label} title={description ?? ''}>
            <StatusPill
              onClick={() => setActiveFilter(label)}
              sx={{
                background:
                  activeFilter === label ? mainColors.MAIN_LIGHT : '#fff',
                border: `1px solid ${
                  activeFilter === label
                    ? mainColors.MAIN
                    : secondaryColors.SECONDARY_6
                }`,
                color:
                  activeFilter === label
                    ? mainColors.MAIN
                    : secondaryColors.SECONDARY
              }}
            >
              {label}
              {(statCounts[label] ?? 0) > 0 && (
                <Box
                  component="span"
                  sx={{
                    background:
                      activeFilter === label
                        ? mainColors.MAIN
                        : secondaryColors.SECONDARY_6,
                    borderRadius: '10px',
                    color:
                      activeFilter === label
                        ? '#fff'
                        : secondaryColors.SECONDARY,
                    fontSize: '10px',
                    fontWeight: 700,
                    px: '6px',
                    py: '1px'
                  }}
                >
                  {statCounts[label]}
                </Box>
              )}
            </StatusPill>
          </Tooltip>
        ))}

        <Box sx={{ flex: 1 }} />

        <AutoCompleteSearch
          options={options}
          setOptions={setOptions}
          setSearch={setTextSearchFilter}
          setValueSearched={setTextSearchFilter}
          value={textSearchFilter}
        />

        <Select
          onChange={(e) => {
            setSortField(e.target.value as string);
            setSortDir('asc');
          }}
          size="small"
          sx={{ fontSize: '12.5px', height: '34px' }}
          value={sortField}
        >
          <MenuItem value="leadProjectStartDate">Sort: Start Date</MenuItem>
          <MenuItem value="leadProjectEndDate">Sort: End Date</MenuItem>
          <MenuItem value="status">Sort: Status</MenuItem>
          <MenuItem value="client">Sort: Client</MenuItem>
          <MenuItem value="leadName">Sort: Name</MenuItem>
        </Select>

        <Box
          sx={{
            background: secondaryColors.SECONDARY_6,
            borderRadius: '8px',
            display: 'flex',
            gap: '2px',
            p: '3px'
          }}
        >
          <ViewToggleBtn
            onClick={() => setViewMode('grid')}
            size="small"
            sx={{
              background: viewMode === 'grid' ? '#fff' : 'transparent',
              boxShadow:
                viewMode === 'grid' ? '0 1px 3px rgba(15,28,46,.08)' : 'none',
              color:
                viewMode === 'grid'
                  ? mainColors.MAIN
                  : secondaryColors.SECONDARY_1
            }}
          >
            <GridViewIcon sx={{ fontSize: 16 }} />
          </ViewToggleBtn>
          <ViewToggleBtn
            onClick={() => setViewMode('list')}
            size="small"
            sx={{
              background: viewMode === 'list' ? '#fff' : 'transparent',
              boxShadow:
                viewMode === 'list' ? '0 1px 3px rgba(15,28,46,.08)' : 'none',
              color:
                viewMode === 'list'
                  ? mainColors.MAIN
                  : secondaryColors.SECONDARY_1
            }}
          >
            <ListIcon sx={{ fontSize: 16 }} />
          </ViewToggleBtn>
          <ViewToggleBtn
            onClick={() => setViewMode('kanban')}
            size="small"
            sx={{
              background: viewMode === 'kanban' ? '#fff' : 'transparent',
              boxShadow:
                viewMode === 'kanban' ? '0 1px 3px rgba(15,28,46,.08)' : 'none',
              color:
                viewMode === 'kanban'
                  ? mainColors.MAIN
                  : secondaryColors.SECONDARY_1
            }}
          >
            <ViewKanbanOutlinedIcon sx={{ fontSize: 16 }} />
          </ViewToggleBtn>
        </Box>
      </ToolbarBox>

      {isValidating && dataProject.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {(!isValidating || dataProject.length > 0) && (
        <>
          {viewMode === 'list' && renderList()}
          {viewMode === 'grid' && renderGrid()}
          {viewMode === 'kanban' && renderKanban()}
          {viewMode !== 'kanban' && renderPagination()}
        </>
      )}
    </PageContainer>
  );
};

export default ListingProjects;
